import React, { useMemo, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { OrgDTO, OrgDTOFromJSON, TeamDTO, TeamDTOFromJSON } from '@atlassiansox/phobos-client-ts';
import { useQuery } from '@tanstack/react-query';
import { ExperienceSuccess as UFOExperienceSuccess } from '@atlassian/ufo';
import Button from '@atlaskit/button';
import qs from 'query-string';

import { apiFetch } from '@/services';
import { ActionButtons, EntityPageSkeleton, Item } from '@/components/primitive';
import apiMeta from '@/meta';
import { WithPageTitle } from '@/components/primitive/with-page-title';
import { ExperienceErrorBoundary } from '@/components/primitive/error-boundary';
import {
  marginAuto,
  spaceTopComfortable,
  spaceTopCompact,
  breakWord,
  positionRelative,
} from '@/styles';
import {
  useFetchErrorController,
  FilterStateProvider,
  useOrgUserController,
  useLocationController,
  useNavigationController,
} from '@/controllers';
import { FetchError } from '@/services/api-service/api-service';
import { PageContentTabs } from '@/components/composed';
import { PageContentTabNames } from '@/components/composed/page-content-tabs/constants';
import { querySpec } from '@/services/api-service/api-queries';
import TeamOrgModel from '@/components/composed/team-org-model';
import TeamPurge from '@/components/composed/team-purge';
import OnboardingPopup from '@/components/composed/onboarding-popup';
import { UFOExperienceContext, useRestartUFOComponentExperience } from '@/common/ufo';
import { teamViewExperience } from '@/common/ufo/experiences';
import SfxIcon from '@/components/primitive/sfx-icon';

import { contentDashboardStyles, sectionMainStyles, sectionTopStyles } from '../dashboard/styles';

export type TeamViewProps = {
  tabName?: PageContentTabNames;
};

const TeamView: React.FC<TeamViewProps> = ({ tabName }) => {
  const { publicId } = useParams<{ publicId: string }>();

  const { search } = useLocation();
  const parsedSearch = qs.parse(search);

  const { isDashboard } = useLocationController();
  const { teams } = useOrgUserController();
  const { onNavigation } = useNavigationController();

  const isCurrentTeamDashboard = isDashboard && teams?.some(x => x.publicId === publicId);
  const hasOnlyOneTeam = teams?.length === 1;

  const { onError } = useFetchErrorController();
  const { isInitialLoading: teamIsLoading, data: rawTeamData, error } = useQuery(
    ['team', publicId],
    () => apiFetch(`${apiMeta.team.endpoints.resource}/${publicId}`),
    { onError }
  );

  const { queryKey, path } = querySpec('org.{id}', { id: rawTeamData?.atlassianOrgId });
  const { data: rawOrgData } = useQuery(queryKey, () => apiFetch(path), {
    onError,
    enabled: !!rawTeamData?.atlassianOrgId,
  });

  const dataOrg: OrgDTO = useMemo(() => OrgDTOFromJSON(rawOrgData || {}), [rawOrgData]);
  const data: TeamDTO = useMemo(() => TeamDTOFromJSON(rawTeamData || {}), [rawTeamData]);

  const hasMainSection = data.description || data.opsgenieTeam || data.atlassianOrgStatus;

  useEffect(() => {
    if (!teamIsLoading && error instanceof FetchError && error.response.status === 404) {
      onNavigation('/404');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, teamIsLoading]);

  const teamDetectorSearch = React.useCallback(() => {
    const searchName = encodeURIComponent(`Team ${data.name}`);
    const url = `https://atlassian.signalfx.com/#/detectors/DrE8IrcAwAA?page=1&limit=20&name=${searchName}&sort=-lastUpdated`;
    return !!url && window.open(url, '_blank')?.focus();
  }, [data]);

  if (!rawTeamData || teamIsLoading) return <EntityPageSkeleton />;

  const allowPurgeTeam =
    __DEPLOY_ENV__ !== 'production' &&
    __DEPLOY_ENV__ !== 'prod' &&
    (isCurrentTeamDashboard || __DEPLOY_ENV__ === 'local' || parsedSearch.allowPurge === 'on');

  return (
    <ExperienceErrorBoundary>
      <WithPageTitle title={data.name} scope="Team">
        {isCurrentTeamDashboard && hasOnlyOneTeam && (
          <OnboardingPopup onboardingKey="tome-onboarding-user-redirect">
            <div>
              This is not your team?
              <br />
              Please review and update the &ldquo;Atlassian Org Team&rdquo; assignment section on
              your Tome team page.
            </div>
          </OnboardingPopup>
        )}
        <UFOExperienceSuccess experience={teamViewExperience} />
        <div
          css={[marginAuto, isDashboard ? contentDashboardStyles : '']}
          data-testid="team-focus-container"
        >
          <section css={sectionTopStyles}>
            <Item title={`${isCurrentTeamDashboard ? 'my ' : ''}team`}>
              <div css={[spaceTopCompact, breakWord]}>
                <h3>{data.name}</h3>
              </div>
            </Item>
            <div>
              <Button onClick={() => teamDetectorSearch()} iconBefore={<SfxIcon />}>
                Search for detectors
              </Button>
              <ActionButtons id={data.publicId} entityName={data.name} type="team" />
            </div>
          </section>
          {hasMainSection && (
            <section css={sectionMainStyles}>
              {data.atlassianOrgStatus && <TeamOrgModel data={data} dataOrg={dataOrg} />}
              {data.description && (
                <Item title="Description">
                  <p>{data.description}</p>
                </Item>
              )}
              {data.opsgenieTeam && (
                <Item title="OpsGenie Team">
                  <p>{data.opsgenieTeam}</p>
                </Item>
              )}
            </section>
          )}
          <section css={[spaceTopComfortable, positionRelative]}>
            {allowPurgeTeam && <TeamPurge publicId={data.publicId} />}
            <FilterStateProvider>
              <PageContentTabs id={data.publicId} tabName={tabName} entityType="team" />
            </FilterStateProvider>
          </section>
        </div>
      </WithPageTitle>
    </ExperienceErrorBoundary>
  );
};

const TeamViewInstrumented: typeof TeamView = props => {
  useRestartUFOComponentExperience(teamViewExperience, []);

  return (
    <UFOExperienceContext.Provider value={teamViewExperience}>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <TeamView {...props} />
    </UFOExperienceContext.Provider>
  );
};

export default TeamViewInstrumented;
